import Modifier from 'ember-modifier';
import { inject as service } from '@ember/service';
import { registerDestructor, isDestroying } from '@ember/destroyable';

const GOOGLE_OPTIONS = {
  types: ['(cities)'],
};

const findPlace = (obj) => {
  return obj.gm_accessors_.place;
};

const findInput = (obj) => {
  for (const x of Object.values(obj)) {
    if (typeof x === 'object') {
      const input = x?.gm_accessors_?.input;

      if (input) {
        return input;
      }
    }
  }
};

const findPacContainer = (obj) => {
  for (const x of Object.values(obj)) {
    if (typeof x === 'object') {
      for (const y of Object.values(x)) {
        if (y instanceof HTMLElement && y.matches('.pac-container')) {
          return y;
        }
      }
    }
  }
};

const AUTOCOMPLETE_REDUCER = [findPlace, findInput, findPacContainer];

const cleanup = (instance) => {
  if (!instance.autocomplete) {
    return;
  }

  google.maps.event.clearInstanceListeners(instance.autocomplete);

  try {
    AUTOCOMPLETE_REDUCER.reduce((result, func) => func(result), instance.autocomplete).remove();
  } catch {
    // this is a best-effort attempt to clean up after the `google.maps.places.Autocomplete`
    // api which leaves orphaned `div` on the page. if this craps out, it's fine if the divs
    // stay on the page. Google obfuscates their library and provide no documented way to do
    // this in an official manner
  }

  window.removeEventListener('mousedown', instance.handleMouseDown, true);
};

class GooglePlacesModifier extends Modifier {
  autocomplete = null;

  #active;
  #callback;
  #mousedown;
  #initialized = false;

  @service('google-places')
  places;

  handleMouseDown = (e) => {
    if (this.#active && e.target.closest('.pac-item')) {
      this.#mousedown?.();
    }
  };

  constructor() {
    super(...arguments);
    registerDestructor(this, cleanup);
  }

  modify(element, [callback], { mousedown, active = true }) {
    this.#active = active;
    this.#callback = callback;
    this.#mousedown = mousedown;

    if (!this.#initialized) {
      this.#initialize(element);
    }
  }

  async #initialize(element) {
    this.#initialized = true;

    try {
      await this.places.load();
    } catch {
      // error is already logged in the `services/google-places`
      return;
    }

    if (isDestroying(this)) {
      return;
    }

    this.autocomplete = new google.maps.places.Autocomplete(
      element,
      GOOGLE_OPTIONS,
    );

    this.autocomplete.addListener('place_changed', () => {
      if (this.#active) {
        this.#callback(this.autocomplete.getPlace());
      }
    });

    window.addEventListener('mousedown', this.handleMouseDown, true);
  }
}

export default GooglePlacesModifier;
